//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import WeeklyGrowthReportModel from './weeklyGrowthReport.model';

/**
 * @service WeeklyGrowthReportService
 * @description Service for generating and retrieving weekly growth reports within the aquaculture system.
 * This service is responsible for fetching aggregated growth data derived from sampling records.
 *
 * The weekly growth report provides critical insights into the performance of the culture, including:
 * - **Average Body Weight (ABW)** progression.
 * - **Biomass** estimation.
 * - **Feed Conversion Ratio (FCR)** calculations.
 * - **Survival Rate** estimates.
 *
 * It interacts with the `/blubay/sampling/` backend endpoint, specifically the `weeklyGrowthReport` method.
 *
 * **Key Responsibilities:**
 * 1. **Report Generation**: Fetching calculated growth metrics for specific pond sections.
 * 2. **Data Aggregation**: The backend aggregates sampling data to produce weekly summaries.
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Generate weekly growth reports for selected sections.
 * @feature 2. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<WeeklyGrowthReportModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/sampling/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})

export class WeeklyGrowthReportService extends Service<WeeklyGrowthReportModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/sampling/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/sampling/');
  }

  /**
   * @method getWeeklyGrowthReport
   * @async
   * @description Fetch the weekly growth report for specific sections.
   *
   * Sends a POST request containing the section IDs (and potentially other filter criteria) to the backend.
   * The backend calculates growth metrics based on sampling data associated with those sections.
   *
   * @param {any} sectionIDs - The payload containing section IDs or filter criteria for the report.
   * @returns {Promise<Array<WeeklyGrowthReportModel>>} Promise resolving to an array of WeeklyGrowthReportModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `weeklyGrowthReport`.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * const filter = { section_id: ['sec-1', 'sec-2'] };
   * weeklyGrowthReportService.getWeeklyGrowthReport(filter).then(report => {
   *   console.log('Growth Report:', report);
   * });
   */
  public getWeeklyGrowthReport(sectionIDs: any) {
    return new Promise<Array<WeeklyGrowthReportModel>>((resolve, reject) => {
        const request = this.httpClient.post<Array<WeeklyGrowthReportModel>>(`${this.baseUrl}${this.serviceUrl}weeklyGrowthReport`, sectionIDs, this.httpOptions).subscribe(response => {
            resolve(response);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
        this.ongoingRequests.push(request);
      });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
